import * as React from "react";
import { Loader2 } from "lucide-react";
import {
  Button,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
  cn,
} from "@eduviaos/ui-kit";
import type { ApiError } from "@eduviaos/shared";
import {
  SEAT_PLAN_HELP,
  SEAT_PLAN_KINDS,
  SEAT_PLAN_LABELS,
  type EffectiveSeatPlan,
  type LearnerSeatPlanBody,
  type SeatPlanKind,
} from "@/lib/seating/plans";
import { useSeats } from "@/lib/seating/seats";
import { useZones } from "@/lib/seating/zones";
import { sortSlots, useSeatSlots } from "@/lib/seatSlots/api";

/**
 * Phase SC-22c — edit one learner's seat plan, or apply one plan to a
 * selection of learners. Only the fields the chosen kind uses are shown,
 * and only those are sent (the BE 422s the rest). A specific seat cannot
 * be given to several learners at once, so bulk hides the seat picker.
 */

const ANY = "__any__";

type HoursBy = "shifts" | "window";

interface Draft {
  kind: SeatPlanKind;
  zone_id: string;
  seat_id: string;
  hoursBy: HoursBy;
  slot_keys: string[];
  window_start: string;
  window_end: string;
  valid_from: string;
  valid_until: string;
  note: string;
}

function draftFrom(plan: EffectiveSeatPlan | null): Draft {
  return {
    kind: plan?.source === "learner_plan" ? plan.kind : "first_come",
    zone_id: plan?.zone_id ?? "",
    seat_id: plan?.seat_id ?? "",
    hoursBy: plan?.window_start ? "window" : "shifts",
    slot_keys: plan?.slot_keys ?? [],
    window_start: plan?.window_start?.slice(0, 5) ?? "",
    window_end: plan?.window_end?.slice(0, 5) ?? "",
    valid_from: plan?.valid_from ?? "",
    valid_until: plan?.valid_until ?? "",
    note: plan?.note ?? "",
  };
}

function minutes(hhmm: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Client-side mirror of `LearnerSeatPlanBody._check_shape`. */
function planDraftProblem(draft: Draft, bulk: boolean): string | null {
  if (draft.kind === "zone_only" && !draft.zone_id) return "Pick the room this learner sits in.";
  if (draft.kind === "fixed_seat" && !draft.seat_id && !bulk) return "Pick the learner's seat.";
  if (draft.kind === "fixed_seat" && bulk) return "Give permanent seats one learner at a time.";
  if (draft.kind === "fixed_hours") {
    if (draft.hoursBy === "shifts" && draft.slot_keys.length === 0)
      return "Pick at least one shift.";
    if (draft.hoursBy === "window") {
      const start = minutes(draft.window_start);
      const end = minutes(draft.window_end);
      if (start === null || end === null) return "Enter a start and an end time.";
      if (end <= start) return "The hours must end after they start.";
      if (end - start < 60 || end - start > 720) return "Daily hours must be 1 to 12 hours long.";
    }
  }
  if (draft.valid_from && draft.valid_until && draft.valid_from > draft.valid_until) {
    return "The plan must start on or before its end date.";
  }
  return null;
}

function toBody(draft: Draft): LearnerSeatPlanBody {
  return {
    kind: draft.kind,
    zone_id: draft.zone_id || null,
    seat_id: draft.seat_id || null,
    slot_keys: draft.kind === "fixed_hours" && draft.hoursBy === "shifts" ? draft.slot_keys : [],
    window_start:
      draft.kind === "fixed_hours" && draft.hoursBy === "window" ? draft.window_start : null,
    window_end:
      draft.kind === "fixed_hours" && draft.hoursBy === "window" ? draft.window_end : null,
    valid_from: draft.valid_from || null,
    valid_until: draft.valid_until || null,
    note: draft.note || null,
  };
}

export interface SeatPlanSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Learner names the plan is for; more than one means bulk. */
  learnerNames: string[];
  current: EffectiveSeatPlan | null;
  pending: boolean;
  error: ApiError | null;
  onSubmit: (body: LearnerSeatPlanBody) => void;
}

export function SeatPlanSheet({
  open,
  onOpenChange,
  learnerNames,
  current,
  pending,
  error,
  onSubmit,
}: SeatPlanSheetProps) {
  const bulk = learnerNames.length > 1;
  const [draft, setDraft] = React.useState<Draft>(() => draftFrom(current));

  React.useEffect(() => {
    if (open) setDraft(draftFrom(current));
  }, [open, current]);

  const zonesQuery = useZones({ limit: 200 });
  const zones = zonesQuery.data?.items ?? [];
  const seatsQuery = useSeats({
    limit: 200,
    is_active: true,
    zone_id: draft.zone_id || undefined,
  });
  const seats = seatsQuery.data?.items ?? [];
  const slotsQuery = useSeatSlots();
  const slots = sortSlots(slotsQuery.data?.items ?? []).filter((s) => s.is_active);

  const problem = planDraftProblem(draft, bulk);
  const usesZone = draft.kind !== "no_seat";
  const zoneRequired = draft.kind === "zone_only";
  const usesSeat = !bulk && (draft.kind === "fixed_seat" || draft.kind === "fixed_hours");

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  const title = bulk
    ? `Seat plan for ${learnerNames.length} learners`
    : `Seat plan for ${learnerNames[0] ?? "learner"}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-md">
        <SheetHeader className="pb-4">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            {bulk
              ? "Every selected learner gets the same plan. Their existing plans are replaced."
              : "How this learner uses seats. The centre default applies when no plan is set."}
          </SheetDescription>
        </SheetHeader>

        <form
          className="grid gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!problem) onSubmit(toBody(draft));
          }}
        >
          <RadioGroup
            value={draft.kind}
            onValueChange={(v) => set("kind", v as SeatPlanKind)}
            className="grid gap-2"
            aria-label="Plan"
          >
            {SEAT_PLAN_KINDS.map((kind) => (
              <div
                key={kind}
                className={cn(
                  "border-border/60 flex items-start gap-3 rounded-md border px-3 py-2.5 transition-colors duration-150 ease-out",
                  draft.kind === kind && "border-primary bg-primary/5",
                )}
              >
                <RadioGroupItem id={`plan-kind-${kind}`} value={kind} className="mt-0.5" />
                <Label htmlFor={`plan-kind-${kind}`} className="grid cursor-pointer gap-0.5">
                  <span className="text-sm font-medium">{SEAT_PLAN_LABELS[kind]}</span>
                  <span className="text-muted-foreground text-xs font-normal">
                    {SEAT_PLAN_HELP[kind]}
                  </span>
                </Label>
              </div>
            ))}
          </RadioGroup>

          {usesZone ? (
            <div className="grid gap-1.5">
              <Label htmlFor="plan-zone">{zoneRequired ? "Room" : "Room (optional)"}</Label>
              <Select
                value={draft.zone_id || ANY}
                onValueChange={(v) => {
                  setDraft((prev) => ({
                    ...prev,
                    zone_id: v === ANY ? "" : v,
                    seat_id: "",
                  }));
                }}
              >
                <SelectTrigger id="plan-zone" aria-label="Room">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {zoneRequired ? null : <SelectItem value={ANY}>Any room</SelectItem>}
                  {zones.map((z) => (
                    <SelectItem key={z.id} value={z.id}>
                      {z.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {usesSeat ? (
            <div className="grid gap-1.5">
              <Label htmlFor="plan-seat">
                {draft.kind === "fixed_seat" ? "Seat" : "Seat for these hours (optional)"}
              </Label>
              <Select
                value={draft.seat_id || ANY}
                onValueChange={(v) => set("seat_id", v === ANY ? "" : v)}
              >
                <SelectTrigger id="plan-seat" aria-label="Seat">
                  <SelectValue placeholder="Pick a seat" />
                </SelectTrigger>
                <SelectContent>
                  {draft.kind === "fixed_hours" ? (
                    <SelectItem value={ANY}>Any free seat</SelectItem>
                  ) : null}
                  {seats.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.seat_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {draft.kind === "fixed_hours" ? (
            <fieldset className="grid gap-3">
              <legend className="mb-1.5 text-sm font-medium">Hours</legend>
              <RadioGroup
                value={draft.hoursBy}
                onValueChange={(v) => set("hoursBy", v as HoursBy)}
                className="flex flex-wrap gap-4"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="hours-by-shifts" value="shifts" />
                  <Label htmlFor="hours-by-shifts">Named shifts</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="hours-by-window" value="window" />
                  <Label htmlFor="hours-by-window">Daily hours</Label>
                </div>
              </RadioGroup>
              {draft.hoursBy === "shifts" ? (
                <div className="grid gap-2">
                  {slots.length === 0 ? (
                    <p className="text-muted-foreground text-xs">
                      No active shifts. Add shifts on the Seat assignment page.
                    </p>
                  ) : (
                    slots.map((slot) => {
                      const checked = draft.slot_keys.includes(slot.key);
                      return (
                        <label key={slot.key} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) =>
                              set(
                                "slot_keys",
                                v
                                  ? [...draft.slot_keys, slot.key]
                                  : draft.slot_keys.filter((k) => k !== slot.key),
                              )
                            }
                            aria-label={slot.label}
                          />
                          <span>{slot.label}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div className="grid gap-1.5">
                    <Label htmlFor="plan-window-start">From</Label>
                    <Input
                      id="plan-window-start"
                      type="time"
                      value={draft.window_start}
                      onChange={(e) => set("window_start", e.target.value)}
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="plan-window-end">Until</Label>
                    <Input
                      id="plan-window-end"
                      type="time"
                      value={draft.window_end}
                      onChange={(e) => set("window_end", e.target.value)}
                    />
                  </div>
                </div>
              )}
            </fieldset>
          ) : null}

          <div className="grid grid-cols-2 gap-2">
            <div className="grid gap-1.5">
              <Label htmlFor="plan-valid-from">Starts (optional)</Label>
              <Input
                id="plan-valid-from"
                type="date"
                value={draft.valid_from}
                onChange={(e) => set("valid_from", e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="plan-valid-until">Ends (optional)</Label>
              <Input
                id="plan-valid-until"
                type="date"
                value={draft.valid_until}
                onChange={(e) => set("valid_until", e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="plan-note">Note (optional)</Label>
            <Textarea
              id="plan-note"
              rows={2}
              maxLength={500}
              value={draft.note}
              onChange={(e) => set("note", e.target.value)}
            />
          </div>

          {problem ? (
            <p className="text-muted-foreground text-xs" data-testid="seat-plan-problem">
              {problem}
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="text-destructive text-xs">
              {error.message || "Couldn't save the plan."}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pb-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending || problem !== null}>
              {pending ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
              {pending ? "Saving…" : bulk ? `Apply to ${learnerNames.length}` : "Save plan"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
